import React from 'react';
import { AppIcon, IconName } from '../../../components/icons/AppIcon';
import type { AlertSeverity } from '../hooks/useSubscriptionAlert';

interface SubscriptionCountdownProps {
    daysRemaining: number;
    severity: AlertSeverity;
    label?: string;
    showProgressBar?: boolean;
    progressPercentage?: number;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

const severityStyles: Record<AlertSeverity, { bg: string; text: string; progressBg: string; progressFill: string; icon: IconName }> = {
    info: { bg: 'bg-blue-50', text: 'text-blue-700', progressBg: 'bg-blue-100', progressFill: 'bg-blue-500', icon: 'clock' },
    warning: { bg: 'bg-amber-50', text: 'text-amber-700', progressBg: 'bg-amber-100', progressFill: 'bg-amber-500', icon: 'clock' },
    critical: { bg: 'bg-red-50', text: 'text-red-700', progressBg: 'bg-red-100', progressFill: 'bg-red-500', icon: 'alert-triangle' },
    blocked: { bg: 'bg-red-100', text: 'text-red-800', progressBg: 'bg-red-200', progressFill: 'bg-red-600', icon: 'x-circle' },
};

const sizeStyles = {
    sm: { container: 'px-2 py-1 rounded', text: 'text-xs', icon: 'w-3 h-3', number: 'text-sm font-semibold' },
    md: { container: 'px-3 py-1.5 rounded-md', text: 'text-sm', icon: 'w-4 h-4', number: 'text-lg font-bold' },
    lg: { container: 'px-4 py-2 rounded-lg', text: 'text-base', icon: 'w-5 h-5', number: 'text-2xl font-bold' },
};

const SubscriptionCountdown: React.FC<SubscriptionCountdownProps> = ({
    daysRemaining,
    severity,
    label = 'dias restantes',
    showProgressBar = false,
    progressPercentage = 0,
    size = 'md',
    className = '',
}) => {
    const styles = severityStyles[severity];
    const sizes = sizeStyles[size];

    return (
        <div className={`${styles.bg} ${sizes.container} ${className}`}>
            <div className="flex items-center gap-2">
                <AppIcon name={styles.icon} className={`${sizes.icon} ${styles.text}`} />
                <div className="flex items-baseline gap-1">
                    <span className={`${sizes.number} ${styles.text}`}>
                        {daysRemaining === 0 ? '!' : daysRemaining}
                    </span>
                    <span className={`${sizes.text} ${styles.text} opacity-80`}>
                        {daysRemaining === 0 ? 'Último dia' : label}
                    </span>
                </div>
            </div>

            {showProgressBar && (
                <div className="progress-wrap mt-2">
                    <div
                        className={`progress-fill ${severity === 'critical' || severity === 'blocked' ? 'progress-fill--danger' : severity === 'warning' ? 'progress-fill--warning' : ''}`.trim()}
                        style={{ width: `${Math.max(5, progressPercentage)}%` }}
                    />
                </div>
            )}
        </div>
    );
};

export default SubscriptionCountdown;
